import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field } from "@/components/dash";

export const BUSINESS_TYPES = [
  { v: "individual", l: "Individual / Seller" },
  { v: "proprietorship", l: "Proprietorship" },
  { v: "partnership", l: "Partnership firm" },
  { v: "pvt_ltd", l: "Private Limited" },
  { v: "llp", l: "LLP" },
];

export const emptyApplication = () => ({
  store_name: "", business_type: "proprietorship", pan_number: "", gst_number: "", contact_phone: "",
  address_line1: "", city: "", state: "", pincode: "", categories: [], description: "",
  account_holder: "", account_number: "", account_number2: "", ifsc: "", bank_name: "",
});

export const fromApplication = (a) => ({
  ...emptyApplication(), ...a,
  gst_number: a.gst_number || "", description: a.description || "", categories: a.categories || [],
  account_holder: a.bank?.account_holder || "", account_number: a.bank?.account_number || "",
  account_number2: a.bank?.account_number || "", ifsc: a.bank?.ifsc || "", bank_name: a.bank?.bank_name || "",
});

// returns an error string, or null when OK
export function checkApplication(f) {
  if (f.account_number !== f.account_number2) return "Bank account numbers do not match.";
  return null;
}

export const toPayload = (f) => ({
  store_name: f.store_name.trim(), business_type: f.business_type,
  pan_number: f.pan_number.trim().toUpperCase(), gst_number: f.gst_number.trim().toUpperCase(),
  contact_phone: f.contact_phone.trim(), address_line1: f.address_line1.trim(), city: f.city.trim(),
  state: f.state.trim(), pincode: f.pincode.trim(), categories: f.categories, description: f.description.trim(),
  bank: {
    account_holder: f.account_holder.trim(), account_number: f.account_number.replace(/\s+/g, ""),
    ifsc: f.ifsc.trim().toUpperCase(), bank_name: f.bank_name.trim(),
  },
});

function Group({ title, desc, children }) {
  return (
    <fieldset className="space-y-4">
      <div className="border-b border-border pb-2">
        <legend className="font-display text-lg font-bold text-deep">{title}</legend>
        {desc && <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>}
      </div>
      {children}
    </fieldset>
  );
}

export default function VendorApplyForm({ form, setForm }) {
  const [cats, setCats] = useState([]);
  useEffect(() => { api.get("/categories").then((r) => setCats(r.data)).catch(() => {}); }, []);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggleCat = (slug) => setForm((f) => ({
    ...f, categories: f.categories.includes(slug) ? f.categories.filter((c) => c !== slug) : [...f.categories, slug],
  }));

  return (
    <div className="space-y-7" data-testid="vendor-apply-form">
      <Group title="Business details" desc="This is what we verify before your store goes live.">
        <Field label="Store / brand name">
          <Input value={form.store_name} onChange={set("store_name")} required minLength={3} maxLength={60} data-testid="va-store-name" />
        </Field>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Business type">
            <Select value={form.business_type} onValueChange={(v) => setForm((f) => ({ ...f, business_type: v }))}>
              <SelectTrigger data-testid="va-business-type"><SelectValue /></SelectTrigger>
              <SelectContent>{BUSINESS_TYPES.map((b) => <SelectItem key={b.v} value={b.v}>{b.l}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Business contact number">
            <Input value={form.contact_phone} onChange={set("contact_phone")} inputMode="numeric" placeholder="10-digit mobile" required data-testid="va-phone" />
          </Field>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="PAN number" hint="Business or owner PAN">
            <Input value={form.pan_number} onChange={(e) => setForm((f) => ({ ...f, pan_number: e.target.value.toUpperCase() }))} maxLength={10} placeholder="ABCDE1234F" required data-testid="va-pan" />
          </Field>
          <Field label="GSTIN (optional)" hint="Leave blank if you're not GST registered">
            <Input value={form.gst_number} onChange={(e) => setForm((f) => ({ ...f, gst_number: e.target.value.toUpperCase() }))} maxLength={15} placeholder="29ABCDE1234F1Z5" data-testid="va-gst" />
          </Field>
        </div>
        <Field label="What do you sell?">
          <div className="flex flex-wrap gap-2">
            {cats.map((c) => (
              <button type="button" key={c.slug} onClick={() => toggleCat(c.slug)} data-testid={`va-cat-${c.slug}`}
                className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${form.categories.includes(c.slug) ? "bg-primary text-white border-primary" : "bg-white border-border hover:border-primary/50"}`}>
                {c.name}
              </button>
            ))}
          </div>
        </Field>
        <Field label="About your store (optional)">
          <Textarea value={form.description} onChange={set("description")} rows={3} maxLength={500} />
        </Field>
      </Group>

      <Group title="Pickup / registered address">
        <Field label="Address">
          <Input value={form.address_line1} onChange={set("address_line1")} required minLength={5} data-testid="va-address" />
        </Field>
        <div className="grid sm:grid-cols-3 gap-4">
          <Field label="City"><Input value={form.city} onChange={set("city")} required data-testid="va-city" /></Field>
          <Field label="State"><Input value={form.state} onChange={set("state")} required data-testid="va-state" /></Field>
          <Field label="Pincode"><Input value={form.pincode} onChange={set("pincode")} inputMode="numeric" maxLength={6} required data-testid="va-pincode" /></Field>
        </div>
      </Group>

      <Group title="Bank details for payouts" desc="Your earnings are paid to this account. Double-check the numbers.">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Account holder name"><Input value={form.account_holder} onChange={set("account_holder")} required data-testid="va-holder" /></Field>
          <Field label="Bank name"><Input value={form.bank_name} onChange={set("bank_name")} required data-testid="va-bank" /></Field>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Account number"><Input value={form.account_number} onChange={set("account_number")} inputMode="numeric" required data-testid="va-acc" /></Field>
          <Field label="Confirm account number"><Input value={form.account_number2} onChange={set("account_number2")} inputMode="numeric" required data-testid="va-acc2" /></Field>
        </div>
        <Field label="IFSC code">
          <Input value={form.ifsc} onChange={(e) => setForm((f) => ({ ...f, ifsc: e.target.value.toUpperCase() }))} maxLength={11} placeholder="HDFC0001234" required data-testid="va-ifsc" />
        </Field>
      </Group>
    </div>
  );
}
